// frontend/src/components/tablekit/useTableKit.js
import * as React from 'react';
import { applyFilters } from './filters';

function sortRows(rows, sort) {
  if (!sort?.key) return rows;
  const { key, dir = 'asc', accessor } = sort;
  const getVal = accessor
    ? (r) => accessor(r)
    : (r) => r[key];

  const arr = rows.slice().sort((a, b) => {
    const va = getVal(a);
    const vb = getVal(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;

    if (typeof va === 'number' && typeof vb === 'number') {
      return va - vb;
    }
    // fallback to string compare
    return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
  });

  return dir === 'desc' ? arr.reverse() : arr;
}

function paginate(rows, page, rowsPerPage) {
  const start = page * rowsPerPage;
  return rows.slice(start, start + rowsPerPage);
}

export function useTableKit({ rows, columns, initialRowsPerPage = 15 }) {
  const [sort, setSort] = React.useState({ key: null, dir: 'asc', accessor: null });
  const [filters, setFilters] = React.useState([]); // [{columnKey, op, value}]
  const [page, setPage] = React.useState(0);
  const [rowsPerPage] = React.useState(initialRowsPerPage);

  const setSortBy = React.useCallback((key, dir = 'asc', accessor = null) => {
    setSort({ key, dir, accessor });
    setPage(0);
  }, []);

  const addFilter = React.useCallback((f) => {
    setFilters((prev) => [...prev, f]);
    setPage(0);
  }, []);
  const updateFilter = React.useCallback((idx, f) => {
    setFilters((prev) => prev.map((x, i) => (i === idx ? f : x)));
    setPage(0);
  }, []);
  const removeFilter = React.useCallback((idx) => {
    setFilters((prev) => prev.filter((_, i) => i !== idx));
    setPage(0);
  }, []);
  const clearFilters = React.useCallback(() => {
    setFilters([]);
    setPage(0);
  }, []);

  const filtered = React.useMemo(
    () => applyFilters(rows || [], filters, columns || []),
    [rows, filters, columns]
  );

  const sorted = React.useMemo(
    () => sortRows(filtered, sort),
    [filtered, sort]
  );

  const pageCount = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const currentPage = Math.min(page, pageCount - 1);
  const visible = React.useMemo(
    () => paginate(sorted, currentPage, rowsPerPage),
    [sorted, currentPage, rowsPerPage]
  );

  const onChangePage = (_, newPage) => setPage(newPage);

  return {
    sort, setSortBy,
    filters, addFilter, updateFilter, removeFilter, clearFilters,
    rowsPerPage, page: currentPage, pageCount, onChangePage,
    allRows: sorted,
    visibleRows: visible,
  };
}
